/**
 * Building blocks shared by the viewer widgets: a link to another entity
 * and the display text of a literal.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { shortenUri } from '@hooks/useEntity';
import { formatLiteralValue, isUriValued } from '@utils/literalFormat.utils';
import { getIntlLocale } from '@utils/locale.utils';
import { uriToDereferencePath } from '@utils/prefix.utils';
import type { EntityObject } from '@/types';
import { localName } from '../linearLocation';
import type { WidgetContext } from './types';
import styles from '../EntityShapeView.module.scss';

/** Friendly text for an IRI: resolved label, else prefixed name. */
export function uriText(uri: string, context: WidgetContext): string {
  const resolved = context.resolved[uri];
  if (resolved && resolved.length > 0) return resolved.join(' / ');
  return partName(uri, context) ?? shortenUri(uri, context.prefixes);
}

/**
 * A part of the object itself (its geometry, its area location) has an id built from the object's
 * own id; without the object's id it reads as what it is: "track combined geom epsg25832".
 */
function partName(uri: string, context: WidgetContext): string | null {
  const own = context.model?.uri;
  if (!own || uri === own) return null;
  const key = localName(own).replace(/^_[a-z]+_/i, '');
  const local = localName(uri);
  if (key.length < 8 || !local.includes(key)) return null;
  return local.replace(key, ' ').replace(/[_\s]+/g, ' ').trim() || null;
}

/** A date as the locale writes it; a value that is no date stays as it is (Intl throws on invalid dates). */
export function formatDate(value: string, language: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(getIntlLocale(language), { dateStyle: 'medium' }).format(date);
}

/** A record's name; a code-like label such as "1" reads with its class: "Track 1". */
export function recordName(uri: string, context: WidgetContext, classLabel?: string | null): string {
  const text = uriText(uri, context);
  return classLabel && /^\S{1,3}$/.test(text) ? `${classLabel} ${text}` : text;
}

/** A link to an entity page; children replace the label (a summary, a picture), never the link. */
export function UriLink({ uri, context, children }: { uri: string; context: WidgetContext; children?: ReactNode }): JSX.Element {
  return (
    <Link to={uriToDereferencePath(uri)} className={styles.valueLink} title={uri}>
      {children ?? uriText(uri, context)}
    </Link>
  );
}

export function useLiteralText(): (value: EntityObject) => string {
  const { t, i18n } = useTranslation();
  const locale = getIntlLocale(i18n.language);
  return (value) =>
    formatLiteralValue(value, locale, t('entity.boolean.yes'), t('entity.boolean.no')) ??
    value.value;
}

/** Any value as text or link, for widgets that accept both. */
export function AnyValue({
  value,
  context,
}: {
  value: EntityObject;
  context: WidgetContext;
}): JSX.Element {
  const literalText = useLiteralText();
  if (isUriValued(value)) return <UriLink uri={value.value} context={context} />;
  return <span>{literalText(value)}</span>;
}
